const TOKEN_PATTERN = /\{([^{}]+)\}/g;

export function fieldName(value) {
  const match = /^\{([^{}]+)\}$/.exec(String(value || "").trim());
  return match ? match[1] : String(value || "");
}

export function resolveRuleValue(value, formValues = {}) {
  if (typeof value !== "string") return value;

  const exactField = fieldName(value);
  if (exactField !== value) return formValues?.[exactField] ?? "";

  return value.replace(TOKEN_PATTERN, (_, name) => formValues?.[name] ?? "");
}

export function collectRules(nodes, type) {
  const rules = [];

  const visit = value => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    if (!value || typeof value !== "object") return;

    if (Array.isArray(value?.data?.rules)) {
      value.data.rules.forEach(rule => {
        if (!type || String(rule?.sType || "").toUpperCase() === type) {
          rules.push(rule);
        }
      });
    }

    visit(value.child);
    visit(value.details);
  };

  visit(nodes);
  return rules;
}

export function ruleDependencyKey(rules = [], formValues = {}) {
  const names = new Set();

  rules.forEach(rule => {
    const sources = [
      rule?.sDataAware,
      rule?.sConditionField,
      ...Object.values(rule?.sMapping || {})
    ];

    sources.forEach(source => {
      if (typeof source !== "string") return;
      for (const match of source.matchAll(TOKEN_PATTERN)) names.add(match[1]);
    });
  });

  return JSON.stringify(
    [...names].sort().map(name => [name, formValues?.[name] ?? ""])
  );
}

export function applyCopyFieldRules(
  currentValues = {},
  rules = [],
  previousMatches = {}
) {
  let nextValues = currentValues;
  const nextMatches = {};

  rules.forEach((rule, index) => {
    const ruleKey = rule?.sRuleName || `COPY_FIELDS:${index}`;
    const conditionValue = resolveRuleValue(rule?.sConditionField, currentValues);
    const expectedValue = rule?.sConditionValue ?? "Yes";
    const matches = String(conditionValue ?? "") === String(expectedValue);
    nextMatches[ruleKey] = matches;

    const shouldClear =
      !matches &&
      rule?.bClearWhenFalse === true &&
      previousMatches?.[ruleKey] === true;

    if (!matches && !shouldClear) return;

    Object.entries(rule?.sMapping || {}).forEach(([target, source]) => {
      const value = matches ? resolveRuleValue(source, currentValues) : "";
      if ((nextValues?.[target] ?? "") === (value ?? "")) return;
      if (nextValues === currentValues) nextValues = { ...currentValues };
      nextValues[target] = value ?? "";
    });
  });

  return { values: nextValues, matches: nextMatches };
}

function actionAllowed(rule, formAction) {
  const actions = Array.isArray(rule?.sApplyOn)
    ? rule.sApplyOn
    : String(rule?.sApplyOn || "")
        .split(",")
        .map(value => value.trim())
        .filter(Boolean);

  return (
    actions.length === 0 ||
    actions.some(action => String(action).toUpperCase() === String(formAction).toUpperCase())
  );
}

export function applyFormToFreeFormRows(
  currentRows = [],
  rules = [],
  formValues = {},
  formAction = ""
) {
  let nextRows = currentRows;

  rules
    .filter(rule =>
      String(rule?.sType || "").toUpperCase() === "SYNC_FORM_TO_ROW" &&
      actionAllowed(rule, formAction)
    )
    .forEach(rule => {
      let rows = nextRows;
      const primaryField = rule?.sPrimaryField;
      const primaryValue = rule?.sPrimaryValue ?? "Yes";
      let targetIndex = primaryField
        ? rows.findIndex(row => String(row?.[primaryField] ?? "") === String(primaryValue))
        : -1;

      if (targetIndex < 0 && rows.length > 0) targetIndex = 0;

      if (targetIndex < 0) {
        if (rule?.bCreateIfMissing !== true) return;
        rows = [{ id: 1, ...(rule?.sDefaults || {}) }];
        targetIndex = 0;
      }

      const currentRow = rows[targetIndex] || {};
      const nextRow = { ...rule?.sDefaults, ...currentRow };
      let changed = false;

      Object.entries(rule?.sMapping || {}).forEach(([target, source]) => {
        const value = resolveRuleValue(source, formValues);
        if ((nextRow?.[target] ?? "") === (value ?? "")) return;
        nextRow[target] = value ?? "";
        changed = true;
      });

      Object.entries(rule?.sDefaults || {}).forEach(([target, value]) => {
        if (currentRow?.[target] !== undefined && currentRow?.[target] !== "") return;
        nextRow[target] = value ?? "";
        if ((currentRow?.[target] ?? "") !== (value ?? "")) changed = true;
      });

      if (rows !== nextRows || changed) {
        nextRows = rows.map((row, index) => (index === targetIndex ? nextRow : row));
      }
    });

  return nextRows;
}
